import React from 'react';
import { ClientPageLayout } from '@/components/client/shared/ClientPageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BotScheduleSettings } from '@/components/client/sections/BotScheduleSettings';
import { BotMessagesSettings } from '@/components/client/sections/BotMessagesSettings';
import { Circle, HelpCircle, Phone } from 'lucide-react';

export function ClientBot() {
  // Datos mock del bot
  const bot = {
    name: 'Bot Assistant Pro',
    description: 'Asistente virtual para atención al cliente',
    daysActive: 45,
    welcomeMessage: '¡Hola! 👋 Soy el asistente virtual de tu empresa. ¿En qué puedo ayudarte hoy?',
    channel: 'whatsapp'
  };
  
  return (
    <ClientPageLayout
      title={bot.name}
      subtitle="Configuración y detalles de tu bot"
      showBackButton={true}
    >
      {/* Estado del Bot - Visual */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center text-4xl">
              🤖
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{bot.name}</h2>
              <p className="text-muted-foreground">{bot.description}</p>
              <div className="flex items-center gap-4 mt-3">
                <Badge variant="outline" className="text-green-600 border-green-600">
                  <Circle className="w-2 h-2 fill-current mr-1" />
                  Activo
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Creado hace {bot.daysActive} días
                </span>
              </div>
            </div>
            
            {/* Estadísticas rápidas */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="text-lg font-bold text-green-700">2,847</div>
                <div className="text-xs text-green-600">Conversaciones</div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-lg font-bold text-blue-700">92%</div>
                <div className="text-xs text-blue-600">Satisfacción</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Información simple en tabs */}
      <Tabs defaultValue="general" className="mt-6">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="horarios">Horarios</TabsTrigger>
          <TabsTrigger value="mensajes">Mensajes</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Nombre del bot</label>
                  <Input value={bot.name} disabled />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Mensaje de bienvenida</label>
                  <Textarea 
                    value={bot.welcomeMessage} 
                    disabled 
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Canal principal</label>
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      W
                    </div>
                    <span>WhatsApp Business</span>
                    <Badge variant="outline" className="ml-auto">Conectado</Badge>
                  </div>
                </div>
                
                {/* Métricas de rendimiento */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Tiempo respuesta promedio</div>
                    <div className="text-xl font-bold">1.2s</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Conversaciones este mes</div>
                    <div className="text-xl font-bold">1,247</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="horarios">
          <BotScheduleSettings />
        </TabsContent>
        
        <TabsContent value="mensajes">
          <BotMessagesSettings />
        </TabsContent>
      </Tabs>

      {/* Acciones simples */}
      <Card>
        <CardHeader>
          <CardTitle>¿Necesitas ayuda?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button variant="outline" className="justify-start">
              <HelpCircle className="w-4 h-4 mr-2" />
              Ver guía de uso
            </Button>
            <Button variant="outline" className="justify-start">
              <Phone className="w-4 h-4 mr-2" />
              Contactar soporte
            </Button>
          </div>
          
          {/* Tips útiles */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">💡 Tips para optimizar tu bot:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Revisa las preguntas frecuentes semanalmente</li>
              <li>• Los horarios de mayor actividad son de 2PM a 6PM</li>
              <li>• Considera activar el bot los domingos para mayor cobertura</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </ClientPageLayout>
  );
}